<template>
  <div class="report-preview">
    <SaveOrDiscardDialog v-if="isReportUpdated && dialog" v-model="dialog">
      <template #content>
        <span v-if="!reportToEdit.title"
          >Your changes will not be saved. If you want to save changes please enter a name:</span
        >
        <span v-else>Do you want to save changes ?</span>

        <q-input v-if="!reportToEdit.title" v-model.trim="reportUpdatedName" label="Report name" dense />
      </template>

      <template #actions>
        <q-btn outline label="Cancel" color="tertiary" @click="dialogCancelled" />

        <q-btn outline label="Discard" color="red" @click="discardChanges" />

        <q-btn
          :loading="isSaveBtnLoading"
          :disable="!reportUpdatedName && !reportToEdit.title"
          outline
          label="Save"
          color="green"
          @click="saveChanges"
        />
      </template>
    </SaveOrDiscardDialog>

    <div class="table">
      <div class="col-xs-12">
        <AgGridWrapper
          ref="agGrid"
          :options="gridOptions"
          :infinite-scroll-request="infiniteScrollRequest"
          :update-rows-count="updateRowsCount"
          @hook:mounted="agGridMounted"
          @drag-stopped="columnDragStopped"
        >
          <template #header-row>
            <TableHeader
              :is-save-btn-loading="isSaveBtnLoading"
              :report-name.sync="reportName"
              @update="update()"
              @generate="generate()"
            />
          </template>
          <template #footerActions>
            <div class="row fit q-pr-md q-pb-md justify-end">Total Rows: {{ totalRows }}</div>
          </template>
        </AgGridWrapper>
      </div>
    </div>
  </div>
</template>

<script>
import { mapMutations, mapActions } from 'vuex'
import { cloneDeep, isEqual } from 'lodash'
import * as apiTo from '@api/api-routes'
import { previewReportMixin } from '@/modules/reports/shared/preview-report-mixin'
// Components
import AgGridWrapper from '@components/ag-grid-helpers/AgGridWrapper'
import TableHeader from '@/modules/reports/components/TableHeader'
import SaveOrDiscardDialog from '@/modules/reports/components/SaveOrDiscardDialog'

export default {
  components: {
    AgGridWrapper,
    TableHeader,
    SaveOrDiscardDialog
  },

  mixins: [previewReportMixin],

  data: () => ({
    isFilterAndSortSet: false,
    dialog: false,
    routeBeforeDialog: null,
    reportUpdatedName: null
  }),

  created() {
    this.setRouterView('editReportPage')
    this.setShowBuildingReportBtn(true)
    this.setShowReportColumns(true)
    window.onbeforeunload = () => {
      if (this.isReportUpdated) {
        return true
      }
    }
  },

  beforeRouteUpdate(to, from, next) {
    if (this.isReportUpdated && !this.routeBeforeDialog) {
      this.routeBeforeDialog = to
      this.dialog = true
    } else {
      const report = this.savedReports.find(r => r.id === Number(to.params.id))
      this.viewReport(report)
      next()
    }
  },

  beforeRouteLeave(to, from, next) {
    if (this.isReportUpdated && !this.routeBeforeDialog) {
      this.routeBeforeDialog = to
      this.dialog = true
    } else {
      this.setShowBuildingReportBtn(false)
      this.setShowReportColumns(false)
      next()
    }
  },

  beforeDestroy() {
    window.onbeforeunload = null
  },

  methods: {
    ...mapMutations('reports', [
      'updateSavedReport',
      'setReportToEdit',
      'setVisibleColumns',
      'setShowBuildingReportBtn',
      'setShowReportColumns',
      'setRouterView',
      'setIsReportUpdated'
    ]),

    ...mapActions('reports', ['viewReport']),

    agGridMounted() {
      const gridOptions = this.$refs.agGrid.getGridOptions()

      gridOptions.api.setFilterModel(this.reportToEdit.filters)
      this.setSort(gridOptions)

      this.isFilterAndSortSet = true
    },

    setSort(gridOptions) {
      const columnState = gridOptions.columnApi.getColumnState()
      const columnWithSorts = columnState.reduce((acc, col) => {
        const copiedColumn = { ...col, sort: this.getSort(col.colId) }
        acc.push(copiedColumn)
        return acc
      }, [])

      gridOptions.columnApi.applyColumnState({ state: columnWithSorts })
    },

    getSort(colId) {
      const columnsWithSorts = this.reportToEdit.sorts
      const colIdsOfSortModel = columnsWithSorts.map(sort => sort.colId)

      if (colIdsOfSortModel.includes(colId)) {
        const column = columnsWithSorts.find(column => column.colId === colId)
        return column.sort
      }
      return null
    },

    infiniteScrollRequest(request) {
      if (!this.isFilterAndSortSet) {
        return new Promise(resolve => resolve(null))
      }
      this.checkFilterAndSortModelChanges()
      return apiTo.getReportTypePreview(this.reportToEdit.report_type.id, {
        ...request,
        selectedColumns: this.selectedColumns()
      })
    },

    checkFilterAndSortModelChanges() {
      const { filterModel, sortModel } = this.getPayload()
      const savedFilters = Array.isArray(this.reportToEdit.filters) ? {} : this.reportToEdit.filters

      if (!isEqual(filterModel, savedFilters)) {
        this.setIsReportUpdated(true)
      } else if (!isEqual(sortModel, this.reportToEdit.sorts)) {
        this.setIsReportUpdated(true)
      } else {
        this.setIsReportUpdated(false)
      }
    },

    dialogCancelled() {
      this.dialog = false
      this.routeBeforeDialog = null
    },

    discardChanges() {
      this.$router.push(this.routeBeforeDialog)
    },

    saveChanges() {
      if (this.reportUpdatedName) {
        const report = cloneDeep(this.reportToEdit)
        report.title = this.reportUpdatedName
        this.reportUpdatedName = null
        this.setReportToEdit(report)
      }
      this.update(() => {
        this.$router.push(this.routeBeforeDialog)
      })
    },

    update(callback) {
      const savePayload = this.getSaveReportPayload()

      this.isSaveBtnLoading = true
      apiTo
        .updateReport(this.reportToEdit.id, {
          title: this.reportName,
          ...savePayload
        })
        .then(({ data }) => this.updateSavedReport(data.result))
        .finally(() => {
          this.isSaveBtnLoading = false
          if (callback) callback()
        })
    }
  }
}
</script>

<style lang="stylus" scoped>
@import '~@/modules/reports/shared/styles'

agGridTableStyle()
</style>
